import { useNavigate, useParams } from 'react-router-dom';
import { PasswordTips } from '../ChangePassword/components';
import { useCallback, useEffect, useState } from 'react';
import { ChangePasswordError } from 'user/models/errors/ChangePasswordError';
import { Col, Container, Row } from 'react-bootstrap';
import { useRootLoadingSpinner } from 'shared/spinner';
import { HOME_PATH } from 'navigation/routes';
import {
  sendResetPassword,
  validateRecoverPasswordToken,
} from './services/resetPassword';
import { ResetPasswordForm } from './components/RecoverChangePasswordForm.tsx/ResetPasswordForm';
import { Button } from '@orbita-ui/core';
import { Card } from 'shared/components';
import { RECOVER_PASSWORD_PATH } from 'user/routes';
import { TextPlaceholder } from 'shared/placeholders';
import { useAuth } from 'auth/hooks/useAuth';

enum ResetPasswordState {
  INIT = 'init',
  LOADING = 'loading',
  ERROR = 'error',
  SUCCESS = 'success',
}

export function ResetPasswordPage() {
  const [pageState, setPageState] = useState(ResetPasswordState.INIT);
  const navigate = useNavigate();
  const { isLogged, logout } = useAuth();
  let { token } = useParams();
  const [changePasswordError, setChangePasswordError] =
    useState<ChangePasswordError>(null);
  const { setRootLoadingSpinnerStatus } = useRootLoadingSpinner();

  const fetchData = useCallback(async () => {
    try {
      setPageState(ResetPasswordState.LOADING);

      await validateRecoverPasswordToken({ token });
      setPageState(ResetPasswordState.INIT);
    } catch (e) {
      setPageState(ResetPasswordState.ERROR);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (isLogged()) {
      logout();
    }
  }, [isLogged, logout]);

  const handleSubmit = async ({ newPassword, confirmPassword }) => {
    try {
      setChangePasswordError(null);
      setRootLoadingSpinnerStatus(true);
      await sendResetPassword({ newPassword, confirmPassword, userToken: token });
      setPageState(ResetPasswordState.SUCCESS);
    } catch (e) {
      setChangePasswordError(e.message as ChangePasswordError);
    } finally {
      setRootLoadingSpinnerStatus(false);
    }
  };

  if (pageState === ResetPasswordState.LOADING) {
    return (
      <Container>
        <Card>
          <Card.Body $padding>
            <TextPlaceholder />
            <TextPlaceholder />
            <TextPlaceholder />
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (pageState === ResetPasswordState.ERROR) {
    return (
      <Container>
        <Row>
          <Card variant="warning">
            <Row className="mt-1 mb-3">
              <Col>
                El token ingresado no es válido. Para recuperar la clave de su usuario,
                haga click en el botón "Recuperar clave"
              </Col>
            </Row>
            <Row>
              <Col className="mb-2 text-end">
                <Button color="inverted" onClick={() => navigate(HOME_PATH)}>
                  Ir al login
                </Button>
                <Button onClick={() => navigate(RECOVER_PASSWORD_PATH)} className="ms-2">
                  Recuperar clave
                </Button>
              </Col>
            </Row>
          </Card>
        </Row>
      </Container>
    );
  }

  if (pageState === ResetPasswordState.SUCCESS) {
    return (
      <Container>
        <Row>
          <Card variant="info">
            <Row className="mt-1 mb-3">
              <Col>
                Tu clave ha sido modificada exitosamente. Puedes iniciar sesión con tu
                nueva clave.
              </Col>
            </Row>
            <Row>
              <Col className="mb-2 text-end">
                <Button onClick={() => navigate(HOME_PATH)}>Continuar</Button>
              </Col>
            </Row>
          </Card>
        </Row>
      </Container>
    );
  }

  return (
    <Container>
      <Row>
        <Col>
          <PasswordTips />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <ResetPasswordForm
            onSubmit={handleSubmit}
            changePasswordError={changePasswordError}
          />
        </Col>
      </Row>
    </Container>
  );
}
